import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import {
  createCustomTheme as persistCustomTheme,
  deleteCustomTheme as removeCustomTheme,
  injectCustomThemeStyles,
  isSavedThemeId,
  MAX_CUSTOM_THEMES,
  readCustomThemes,
  updateCustomThemeColors,
} from '../lib/customThemes'
import { syncThemeColorOverrides, writeThemeColorOverrides } from '../lib/themeColorTokens'
import { getBuiltInThemeIds, isCustomThemeId, THEME_STORAGE_KEY } from '../lib/themes'

const ThemeContext = createContext(null)

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'maya'

  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY)
  if (savedTheme && isSavedThemeId(savedTheme)) return savedTheme

  return 'maya'
}

const applyThemeToDocument = (theme) => {
  document.documentElement.setAttribute('data-theme', theme)
  window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  syncThemeColorOverrides(theme)
}

export const ThemeProvider = ({ children }) => {
  const [customThemes, setCustomThemes] = useState(() => readCustomThemes())
  const [theme, setThemeState] = useState(() => {
    const initial = getInitialTheme()
    if (typeof document !== 'undefined') {
      injectCustomThemeStyles()
      document.documentElement.setAttribute('data-theme', initial)
    }
    return initial
  })

  const refreshCustomThemes = useCallback(() => {
    setCustomThemes(readCustomThemes())
  }, [])

  useLayoutEffect(() => {
    injectCustomThemeStyles()
    applyThemeToDocument(theme)
  }, [theme])

  const setTheme = useCallback((nextTheme) => {
    if (!isSavedThemeId(nextTheme)) return
    applyThemeToDocument(nextTheme)
    setThemeState(nextTheme)
  }, [])

  const createCustomTheme = useCallback(
    ({ label, sourceThemeId, colors, colorSavePayload }) => {
      const result = persistCustomTheme({ label, sourceThemeId, colors })
      if (!result.ok) return result

      if (colorSavePayload) {
        writeThemeColorOverrides(result.id, colorSavePayload)
      }

      refreshCustomThemes()
      applyThemeToDocument(result.id)
      setThemeState(result.id)
      return result
    },
    [refreshCustomThemes]
  )

  const deleteCustomTheme = useCallback(
    (themeId) => {
      if (!isCustomThemeId(themeId)) return false

      removeCustomTheme(themeId)
      writeThemeColorOverrides(themeId, null)
      refreshCustomThemes()

      if (theme === themeId) {
        applyThemeToDocument('maya')
        setThemeState('maya')
      }

      return true
    },
    [refreshCustomThemes, theme]
  )

  const saveCustomThemeColors = useCallback(
    (themeId, colors, colorSavePayload) => {
      if (!isCustomThemeId(themeId)) return false
      updateCustomThemeColors(themeId, colors)
      if (colorSavePayload) {
        writeThemeColorOverrides(themeId, colorSavePayload)
      }
      refreshCustomThemes()
      return true
    },
    [refreshCustomThemes]
  )

  const persistActiveTheme = useCallback(() => {
    if (typeof window === 'undefined' || !isSavedThemeId(theme)) return false

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
      injectCustomThemeStyles()
      syncThemeColorOverrides(theme)
      return true
    } catch {
      return false
    }
  }, [theme])

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      themes: getBuiltInThemeIds(),
      customThemes,
      createCustomTheme,
      deleteCustomTheme,
      saveCustomThemeColors,
      persistActiveTheme,
      canCreateCustomTheme: customThemes.length < MAX_CUSTOM_THEMES,
      isCustomTheme: isCustomThemeId,
    }),
    [
      theme,
      setTheme,
      customThemes,
      createCustomTheme,
      deleteCustomTheme,
      saveCustomThemeColors,
      persistActiveTheme,
    ]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
